import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { UserCircleIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../../shared/hooks/useAuth";
import { useRbac } from "../../../shared/hooks/usePermission";
import { canAccessModule } from "../../../shared/utils/rbac";

const ProfileDropdown = () => {
  const darkMode = useSelector(selectDarkMode);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { permissions, isSuperAdmin } = useRbac();
  const canViewSettings = canAccessModule(isSuperAdmin, permissions, "settings");

  const email = localStorage.getItem("user_email") || "admin@vaxicare.com";
  const userProfile = JSON.parse(localStorage.getItem("user_profile") || "{}");
  let userName = "Admin User";
  if (userProfile?.firstName || userProfile?.lastName) {
    userName = `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim();
  }
  const userRole =userProfile?.assignedRoles?.length ? userProfile.assignedRoles.map(item=>item.name).join(", ") : "Administrator"
  const userInitial = userName.charAt(0).toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 rounded-xl p-2 transition-all duration-200 group"
      >
        <div className="relative">
          <div className="h-10 w-10 bg-[linear-gradient(180deg,#4A9CFF_0%,#4057FE_100%)] rounded-full flex items-center justify-center text-white font-bold shadow-md transition-shadow">
            {userInitial}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 border-2 rounded-full ${
            darkMode ? 'border-gray-800' : 'border-white'
          }`}></div>
        </div>
        <div className="text-left hidden md:block">
          <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{userName}</p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{userRole}</p>
        </div>
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-3 w-72 rounded-2xl shadow-2xl border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          {/* Header */}
          <div className="bg-[linear-gradient(180deg,#4A9CFF_0%,#4057FE_100%)] p-6 text-white">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-2xl ring-4 ring-white/30">
                {userInitial}
              </div>
              <div className="flex-1">
                <p className="text-base font-bold">{userName}</p>
                <p className="text-sm text-white/80 truncate">{email}</p>
                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm">
                  {userRole}
                </span>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-2">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate("/admin/profile");
              }}
              className={`w-full px-4 py-3 text-left text-sm rounded-xl transition-all duration-200 flex items-center space-x-3 group ${
                darkMode ? 'text-gray-200 hover:bg-blue-900/30' : 'text-gray-700 hover:bg-blue-50'
              }`}
            >
              <div className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'bg-blue-900/30 group-hover:bg-blue-900/50' : 'bg-blue-100 group-hover:bg-blue-200'
              }`}>
                <UserCircleIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">My Profile</p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>View and edit profile</p>
              </div>
            </button>

            {canViewSettings && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/admin/settings");
                }}
                className={`w-full px-4 py-3 text-left text-sm rounded-xl transition-all duration-200 flex items-center space-x-3 group ${
                  darkMode ? 'text-gray-200 hover:bg-purple-900/30' : 'text-gray-700 hover:bg-purple-50'
                }`}
              >
                <div className={`p-2 rounded-lg transition-colors ${
                  darkMode ? 'bg-purple-900/30 group-hover:bg-purple-900/50' : 'bg-purple-100 group-hover:bg-purple-200'
                }`}>
                  <Cog6ToothIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Settings</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preferences & privacy</p>
                </div>
              </button>
            )}
          </div>

          {/* Logout */}
          <div className={`border-t p-2 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className={`w-full px-4 py-3 text-left text-sm rounded-xl transition-all duration-200 flex items-center space-x-3 group ${
                darkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <div className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'bg-red-900/30 group-hover:bg-red-900/50' : 'bg-red-100 group-hover:bg-red-200'
              }`}>
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Logout</p>
                <p className={`text-xs ${darkMode ? 'text-red-400/70' : 'text-red-400'}`}>Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
